import type { ReactNode } from 'react'

/*
  A section that folds.

  Distinct from `.disclose`, which is the small inline "and here is a bit more" triangle
  used inside a card. This is the full-width row with a rule across the screen and a plus
  on the right: it divides a screen into parts, where disclose adds a footnote to one.

  Both exist on the minutes at once and they are doing different jobs, so the two patterns
  are worth keeping apart rather than merging into one that is wrong for both.

  ── Uncontrolled on purpose ─────────────────────────────────────────────────

  `open` is set once from `defaultOpen` and never again. `<details>` keeps its own state
  after that, in the browser, which means a fold the owner opened stays open through a
  re-render without this component holding a useState per section or the screen above it
  holding a Set of ids. React only warns about that when `open` is bound to state and no
  handler exists to change it, which is not this.
*/
export function Fold({
  title, meta, defaultOpen = false, children,
}: {
  title: ReactNode
  /** A count, a status, whatever the row is worth saying without being opened. */
  meta?: ReactNode
  defaultOpen?: boolean
  children: ReactNode
}) {
  return (
    <details className="fold" open={defaultOpen}>
      <summary className="fold__head">
        <span className="fold__title">{title}</span>
        {meta !== undefined && meta !== null && <span className="fold__meta">{meta}</span>}
        <span className="fold__sign" aria-hidden />
      </summary>
      <div className="fold__body">{children}</div>
    </details>
  )
}
